import { SubscriptionSnapshot, CompactStatement } from '../models/subscription.snapshot.model';
import { CatalogVocabulary, CatalogFeatureDefinition, CatalogBaseArn } from '../models/catalog.plan.model';

export type CanDenyReason = 'plan' | 'status' | 'role';

export interface CanResult {
  allowed: boolean;
  reason: CanDenyReason | null;
}

const ALLOWED: CanResult = { allowed: true, reason: null };
const DENIED_PLAN: CanResult = { allowed: false, reason: 'plan' };
const DENIED_STATUS: CanResult = { allowed: false, reason: 'status' };
const DENIED_ROLE: CanResult = { allowed: false, reason: 'role' };

// manage covers all other actions
const MANAGE_COVERS = ['read', 'write', 'delete', 'manage', 'execute', 'share'];

// Default letter-to-action mapping — overridden by catalog when available
const DEFAULT_ACTION_MAP: Record<string, string> = {
  r: 'read', w: 'write', d: 'delete', m: 'manage', x: 'execute', s: 'share',
};

// --- Segment-by-segment resource matching (same as server) ---

export function matchResource(pattern: string, arn: string): boolean {
  if (pattern === '*') return true;

  const patternParts = pattern.split(':');
  const arnParts = arn.split(':');

  for (let i = 0; i < patternParts.length; i++) {
    const pp = patternParts[i];
    if (pp === '*') return true; // wildcard covers all remaining segments
    if (i >= arnParts.length) return false; // pattern is longer than ARN
    if (pp !== arnParts[i]) return false;
  }

  // Pattern fully consumed — matches only if ARN is same length
  return patternParts.length === arnParts.length;
}

function anyPatternMatches(patterns: string[], arn: string): boolean {
  for (const p of patterns) {
    if (matchResource(p, arn)) return true;
  }
  return false;
}

// --- Action matching with catalog-driven letter table ---

function expandLetters(letters: string, actionMap: Record<string, string>): string[] {
  const result: string[] = [];
  for (const ch of letters) {
    const action = actionMap[ch];
    if (action) result.push(action);
  }
  return result;
}

function actionMatches(
  stmtLetters: string,
  requestedAction: string,
  actionMap: Record<string, string>
): boolean {
  const expanded = expandLetters(stmtLetters, actionMap);
  if (expanded.includes(requestedAction)) return true;
  if (expanded.includes('manage') && MANAGE_COVERS.includes(requestedAction)) return true;
  return false;
}

// --- Statement evaluation: Deny explicit > Allow explicit > Deny implicit ---

function evaluateStatements(
  statements: CompactStatement[],
  action: string,
  arn: string,
  actionMap: Record<string, string>
): 'allow' | 'deny' | 'none' {
  let hasAllow = false;

  for (const stmt of statements) {
    if (!actionMatches(stmt.a, action, actionMap)) continue;
    if (!anyPatternMatches(stmt.r, arn)) continue;
    if (stmt.e === 'D') return 'deny';
    if (stmt.e === 'A') hasAllow = true;
  }

  return hasAllow ? 'allow' : 'none';
}

// --- Base ARN check ---

function findBaseArnLevel(
  baseArns: CatalogBaseArn[],
  arn: string
): 'always' | 'plan_exempt' | null {
  for (const base of baseArns) {
    if (matchResource(base.pattern, arn)) return base.level;
  }
  return null;
}

// --- Feature-to-ARN coverage ---

function arnCoveredByFeatures(
  arn: string,
  boundaryFeatures: string[],
  featureDefinitions: CatalogFeatureDefinition[]
): boolean {
  for (const featureKey of boundaryFeatures) {
    if (featureKey === '*') return true;
    const def = featureDefinitions.find(f => f.key === featureKey);
    if (!def || !def.enforceable) continue;
    if (anyPatternMatches(def.patterns, arn)) return true;
  }
  return false;
}

// --- Build action map from catalog ---

function buildActionMap(vocabulary: CatalogVocabulary | null | undefined): Record<string, string> {
  if (!vocabulary?.actions?.length) return DEFAULT_ACTION_MAP;
  const map: Record<string, string> = {};
  for (const a of vocabulary.actions) {
    // Server sends { name, letter } — NOT { action, letter }
    const actionName = a.name;
    if (actionName && a.letter) {
      map[a.letter] = actionName;
    }
  }
  // Guard: if the built map has no usable entries (e.g. field mismatch produced
  // all undefined), the vocabulary is effectively broken. Fail open (F1 bis)
  // rather than denying everyone silently.
  if (Object.keys(map).length === 0) {
    console.warn('[can] vocabulary.actions produced an empty action map — failing open');
    return DEFAULT_ACTION_MAP;
  }
  return map;
}

// =======================================================================
// Main entry point
// =======================================================================

export function can(
  snapshot: SubscriptionSnapshot | null,
  action: string,
  arn: string,
  vocabulary?: CatalogVocabulary | null
): CanResult {
  // No snapshot → allow everything, let the server enforce
  if (!snapshot) return ALLOWED;

  // No vocabulary → cannot evaluate (no base ARNs, no feature map, no action
  // letters). Partial evaluation is worse than none — let the server enforce.
  if (!vocabulary?.actions?.length) return ALLOWED;

  const actionMap = buildActionMap(vocabulary);
  const baseArns = vocabulary.base_arns ?? [];
  const featureDefs = vocabulary.features ?? [];

  // --- 1. Base ARN check ---
  const baseLevel = findBaseArnLevel(baseArns, arn);

  // 'always' and 'plan_exempt' both skip the plan boundary (step 2).
  // 'always' also skips the status boundary (step 3).
  // Neither skips the role check (step 4) — an accountant still cannot manage.

  // --- 2. Plan boundary ---
  if (baseLevel !== 'always' && baseLevel !== 'plan_exempt') {
    const features = snapshot.subscription?.boundary?.features;
    if (features && features.length > 0 && featureDefs.length > 0) {
      if (!arnCoveredByFeatures(arn, features, featureDefs)) {
        return DENIED_PLAN;
      }
    }
  }

  // --- 3. Status boundary (action boundary) ---
  if (baseLevel !== 'always') {
    const boundaryActions = snapshot.subscription?.boundary?.actions;
    if (boundaryActions && boundaryActions.length > 0) {
      if (!boundaryActions.includes(action) && !boundaryActions.includes('*')) {
        const manageCovered = boundaryActions.includes('manage') && MANAGE_COVERS.includes(action);
        if (!manageCovered) {
          return DENIED_STATUS;
        }
      }
    }
  }

  // --- 4. Role (user permissions via compact statements) ---
  const statements = snapshot.permissions?.statements;
  if (statements && statements.length > 0) {
    const verdict = evaluateStatements(statements, action, arn, actionMap);
    if (verdict === 'deny') return DENIED_ROLE;
    if (verdict === 'none') return DENIED_ROLE;
  }

  return ALLOWED;
}
